import React from 'react';
import { Container, Grid, Box, Typography, Card, CardContent, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LaunchIcon from '@mui/icons-material/Launch';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useScrollAnimation, fadeInUp, staggerContainer, scaleIn } from '../hooks/useScrollAnimation';

const ToolsSection = () => {
  const [titleRef, titleControls] = useScrollAnimation();
  const [toolsRef, toolsControls] = useScrollAnimation();

  const tools = [
    {
      name: 'KM-Bizz PDF Studio',
      url: 'https://pdf-tool.km-bizz.com',
      icon: <PictureAsPdfIcon />,
      image: '/projects/pdf-tool.png',
      description: 'Professionelle PDF-Tools für alle - kostenlos, ohne Anmeldung. Bearbeiten, konvertieren und verwalten Sie PDFs direkt im Browser mit oeffentlichem Statistik-Dashboard.',
      features: [
        'Image -> PDF Konvertierung',
        'PDF Zusammenfuehren (Merge)',
        'PDF Komprimierung',
        'PDF Bearbeiten (Edit)',
        'Oeffentliches Dashboard',
        'Feedback & Kanban Board',
        'REST API mit Referenz',
      ],
      tech: ['React', 'Spring Boot', 'FastAPI', 'PyMuPDF', 'Docker'],
      status: 'Live',
      color: '#E53E3E',
    },
    {
      name: 'KMB Music',
      url: 'https://music.km-bizz.com',
      icon: <MusicNoteIcon />,
      image: '/projects/kmb-music.png',
      description: 'Spotify-aehnliche Musik-Streaming-Plattform mit Playlists, Musik-Download und professionellem DJ-Mixing. Automatisches Crossfading mit Beat-Sync und BPM-Erkennung.',
      features: [
        'Playlists erstellen & verwalten',
        'Musik Streaming & Download',
        'DJ Mode mit Auto-Crossfade',
        'Beat Sync & BPM-Erkennung',
        'Online & lokale Playlists',
        'HTTP Range Streaming (206)',
      ],
      tech: ['React', 'ASP.NET Core', 'Nextcloud', 'nginx', 'Docker'],
      status: 'Live',
      color: '#1DB954',
    },
    {
      name: 'MyFinance',
      url: 'https://finance.km-bizz.com',
      icon: <AccountBalanceWalletIcon />,
      image: '/projects/myfinance.png',
      description: 'Multi-Tenant Finanzverwaltung mit Kundenverwaltung, Transaktions-Tracking und detaillierten Analysen. Einnahmen/Ausgaben-Uebersicht mit Trend-Charts pro Kunde.',
      features: [
        'Kunden verwalten (Multi-Tenant)',
        'Transaktionen pro Kunde',
        'Einnahmen/Ausgaben-Analyse',
        'Monatliche Charts & Net-Trend',
        'JWT-Authentifizierung',
        'Nextcloud Beleg-Speicher',
      ],
      tech: ['.NET 8', 'React', 'PostgreSQL', 'JWT', 'Docker'],
      status: 'Live',
      color: '#3182CE',
    },
    {
      name: 'YT Save',
      url: 'https://youtube.km-bizz.com',
      icon: <CloudDownloadIcon />,
      image: '/projects/yt-save.png',
      description: 'YouTube-Downloader mit direkter Nextcloud-Integration. Suchen Sie YouTube oder fuegen Sie eine Video-ID ein und laden Sie als MP3 oder MP4 direkt in Ihren Cloud-Speicher.',
      features: [
        'YouTube-Suche integriert',
        'Download als MP3 (Audio)',
        'Download als MP4 (bis 1080p)',
        'Direkt zu Nextcloud speichern',
        'Webshare.io Proxy Rotation',
        'yt-dlp Backend',
      ],
      tech: ['React', 'yt-dlp', 'Nextcloud', 'Proxy', 'Docker'],
      status: 'Live',
      color: '#FF0000',
    },
  ];

  return (
    <Box
      id="tools"
      sx={{
        py: { xs: 10, md: 15 },
        background: 'rgba(0, 0, 0, 0.2)',
      }}
    >
      <Container maxWidth="xl">
        <Box
          component={motion.div}
          ref={titleRef}
          initial="hidden"
          animate={titleControls}
          variants={fadeInUp}
          sx={{ mb: 8, textAlign: 'center' }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              mb: 2,
            }}
          >
            Online Tools & Services
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Selbst entwickelte Web-Anwendungen - alle mit CI/CD Pipeline auf eigenem Server-Stack deployed
          </Typography>
        </Box>

        <Grid
          container
          spacing={4}
          component={motion.div}
          ref={toolsRef}
          initial="hidden"
          animate={toolsControls}
          variants={staggerContainer}
        >
          {tools.map((tool, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card
                component={motion.div}
                variants={scaleIn}
                whileHover={{
                  y: -12,
                  transition: { duration: 0.3 },
                }}
                sx={{
                  height: '100%',
                  background: 'var(--card-bg)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid var(--border-subtle)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: tool.url !== '#' ? 'pointer' : 'default',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    borderColor: tool.color,
                    boxShadow: `0 20px 60px ${tool.color}40`,
                    '& .project-image': {
                      transform: 'scale(1.05)',
                    },
                  },
                }}
                onClick={() => {
                  if (tool.url !== '#') {
                    window.open(tool.url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                {/* Project Screenshot */}
                <Box
                  sx={{
                    position: 'relative',
                    height: 240,
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, ${tool.color}30, ${tool.color}10)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {tool.image ? (
                    <Box
                      component="img"
                      className="project-image"
                      src={tool.image}
                      alt={tool.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top',
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  {/* Fallback Icon */}
                  <Box
                    sx={{
                      display: tool.image ? 'none' : 'flex',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& svg': {
                        fontSize: 80,
                        color: tool.color,
                        opacity: 0.5,
                      },
                    }}
                  >
                    {tool.icon}
                  </Box>

                  {/* Status Badge */}
                  <Chip
                    label={tool.status}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      background:
                        tool.status === 'Live'
                          ? 'rgba(72, 187, 120, 0.95)'
                          : 'rgba(237, 137, 54, 0.95)',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      backdropFilter: 'blur(4px)',
                    }}
                  />

                  {/* Icon Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 16,
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: tool.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 16px ${tool.color}60`,
                      '& svg': {
                        fontSize: 28,
                        color: '#FFFFFF',
                      },
                    }}
                  >
                    {tool.icon}
                  </Box>
                </Box>

                <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 1,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {tool.name}
                    {tool.url !== '#' && <LaunchIcon sx={{ fontSize: 20, color: tool.color }} />}
                  </Typography>

                  {tool.url !== '#' && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: tool.color,
                        fontFamily: "'Fira Code', monospace",
                        fontSize: '0.85rem',
                        mb: 2,
                      }}
                    >
                      {tool.url.replace('https://', '')}
                    </Typography>
                  )}

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                      mb: 3,
                    }}
                  >
                    {tool.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 700,
                        mb: 1,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.1em',
                      }}
                    >
                      Features:
                    </Typography>
                    <Grid container spacing={0.5}>
                      {tool.features.map((feature, featureIdx) => (
                        <Grid item xs={12} sm={6} key={featureIdx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: tool.color,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
                            >
                              {feature}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto' }}>
                    {tool.tech.map((tech, techIdx) => (
                      <Chip
                        key={techIdx}
                        label={tech}
                        size="small"
                        sx={{
                          background: `${tool.color}15`,
                          color: tool.color,
                          fontFamily: "'Fira Code', monospace",
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          border: `1px solid ${tool.color}30`,
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box
          sx={{
            mt: 8,
            textAlign: 'center',
            p: 5,
            background: 'var(--card-bg)',
            backdropFilter: 'blur(10px)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 4,
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Benoetigen Sie ein individuelles Tool?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: '600px', mx: 'auto' }}>
            Ich entwickle massgeschneiderte Online-Tools und Web-Anwendungen fuer Ihre spezifischen Anforderungen - vom Konzept bis zum Deployment mit CI/CD.
          </Typography>
          <Box
            component="a"
            href="#contact"
            sx={{
              display: 'inline-block',
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #00F5FF, #FF6B35)',
              color: '#0B0D17',
              textDecoration: 'none',
              borderRadius: 2,
              fontWeight: 700,
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(0, 245, 255, 0.4)',
              },
            }}
          >
            Kontaktieren Sie mich
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ToolsSection;
